/**
 * RasterToolCard — Card 2 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.2.
 *
 * Surfaces every raster control listed in the masterplan: QGIS-style
 * symbology (render mode + color schemes), opacity, band selection,
 * brightness/contrast/saturation, no-data mask, blending, histogram,
 * pixel inspector, clip/mask placeholder, and profile sampling for
 * elevation-like rasters.
 *
 * The card activates when `useViewerStore.sceneSelection.toolCardKind`
 * resolves to `'raster'`. It emits its mutations through
 * `setRasterToolCard` so an adapter mock or test can assert on a stable
 * `RasterToolCardState` shape without touching MapLibre internals.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { EngineId, Layer } from '@geosoft/viewer-sdk';
import {
  Banner,
  Callout,
  Chip,
  tokens,
  type ChipVariant,
} from '@/design';
import {
  TerrainViewSwitch,
  type TerrainViewMode,
} from '@/components/map/TerrainViewSwitch';
import {
  ToolCardShell,
  ToolCardSliderRow,
  ToolCardToggleRow,
} from '@/components/toolcards/ToolCardShell';
import {
  useViewerStore,
  type RasterBlendMode,
  type RasterColorRamp,
} from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import { HistogramPanel, parseBandStats } from './HistogramPanel';
import { RasterMetadataPanel } from './RasterMetadataPanel';
import { RasterSpectralProfile } from './RasterSpectralProfile';
import { PathProfilePanel } from './PathProfilePanel';
import { GeoreferencePanel } from '@/components/georeference/GeoreferencePanel';
import {
  shouldShowGeoreferenceTools,
} from '@/components/georeference/needsGeoreference';
import { readGeoreference } from '@/components/georeference/georeferenceTypes';
import {
  buildClassBreaks,
  buildUniqueValueRows,
  classBreaksCssGradient,
  recolorClassBreaks,
  stretchRangeFromStats,
  type RasterStretchType,
} from './rasterClassify';
import { RasterColorField } from './RasterColorField';
import {
  RASTER_COLOR_RAMP_PRESETS,
  RASTER_RENDER_MODES,
  getRampPreset,
  normalizeRasterRenderMode,
  patchForRasterRenderMode,
  type RasterRenderMode,
} from './rasterSymbology';
import {
  DEFAULT_BACKGROUND_COLOR,
  bboxCacheKey,
  bboxToStatisticsGeoJson,
  resolveDisplayNodata,
  resolveDraBbox,
  type LonLatBbox,
  type RasterBackgroundColorMode,
  type RasterStatisticsSource,
} from './rasterDra';
import { extractCogQuery } from '@/utils/titilerRasterProfile';

const STRETCH_TYPES: readonly { id: RasterStretchType; label: string }[] = [
  { id: 'percent-clip', label: 'Percent clip' },
  { id: 'minmax', label: 'Min–Max' },
  { id: 'stddev', label: 'Std deviation' },
  { id: 'none', label: 'None' },
];

const STATISTICS_SOURCES: readonly {
  id: RasterStatisticsSource;
  label: string;
  description: string;
}[] = [
  {
    id: 'dra',
    label: 'DRA',
    description: 'Dynamic range adjustment uses statistics from the current display extent.',
  },
  {
    id: 'dataset',
    label: 'Dataset',
    description: 'Uses the statistics from the raster dataset.',
  },
];

const BACKGROUND_COLOR_MODES: readonly {
  id: RasterBackgroundColorMode;
  label: string;
  icon: 'block' | 'palette';
}[] = [
  { id: 'transparent', label: 'No color', icon: 'block' },
  { id: 'color', label: 'Custom color', icon: 'palette' },
];

const DRA_DEBOUNCE_MS = 350;

type DeferredSectionId =
  | 'symbology'
  | 'georeference'
  | 'display'
  | 'blending'
  | 'pixel'
  | 'histogram'
  | 'pathProfile'
  | 'sampling'
  | 'information';

/** Sections that hit the network stay closed until the operator opens them. */
const DEFAULT_OPEN_SECTIONS: Readonly<Record<DeferredSectionId, boolean>> = {
  symbology: true,
  georeference: false,
  display: false,
  blending: false,
  // Hover `/cog/point` + stats — closed until expanded.
  pixel: false,
  histogram: false,
  pathProfile: false,
  sampling: false,
  information: false,
};

export type RasterTileHealth =
  | 'rest'
  | 'loading'
  | 'partial'
  | 'failed'
  | 'stale'
  | 'permission-denied';

export interface RasterToolCardProps {
  /** The active raster layer; carries the metadata/provenance facts. */
  layer?: Layer | null;
  /** Tile health for the loading/stale/error banner row. */
  tileHealth?: RasterTileHealth;
  /** Permission-denied banner copy (overrides `tileHealth`). */
  permissionDenied?: string | null;
  /** Read-only flag from the gTwin permission model. */
  readOnly?: boolean;
  /** When the active raster is elevation-like, expose profile sampling. */
  isElevationLike?: boolean;
  /** Active map engine — used for the elevation 2D/3D switch. */
  engine?: EngineId;
  /** Swap MapLibre ↔ Giro3D for elevation rasters (owned by TwinShell). */
  onTerrainViewChange?: (engine: TerrainViewMode) => void;
  /** True while ViewerHost remounts after an engine swap. */
  terrainViewSwitching?: boolean;
  /** Click-to-pick clip / mask geometry (placeholder). */
  onPickClipMask?: () => void;
  /** Click-to-open profile sampling panel. */
  onOpenProfileSampling?: () => void;
  /** Force render outside the tool-card kind gate (for tests / preview). */
  forceVisible?: boolean;
  /** Map host element — used by DRA to read the live viewport extent. */
  container?: HTMLElement | null;
  /** Override which deferred sections start open (tests). */
  initialOpenSections?: Partial<Record<DeferredSectionId, boolean>>;
  /** Persist georeference (control points + affine) onto the scene item. */
  onGeoreferenceCommit?: (layer: Layer) => void;
  'data-testid'?: string;
}

const BLEND_MODES: readonly { id: RasterBlendMode; label: string }[] = [
  { id: 'normal', label: 'Normal' },
  { id: 'multiply', label: 'Multiply' },
  { id: 'screen', label: 'Screen' },
  { id: 'overlay', label: 'Overlay' },
];

const TILE_HEALTH_TO_CHIP: Record<
  RasterTileHealth,
  { label: string; variant: ChipVariant; icon: 'check_circle' | 'pending' | 'sync_problem' | 'error' | 'lock' }
> = {
  rest: { label: 'Tiles ready', variant: 'success', icon: 'check_circle' },
  loading: { label: 'Loading tiles', variant: 'info', icon: 'pending' },
  partial: { label: 'Partial tiles', variant: 'warning', icon: 'sync_problem' },
  failed: { label: 'Tile load failed', variant: 'error', icon: 'error' },
  stale: { label: 'Stale tiles', variant: 'warning', icon: 'sync_problem' },
  'permission-denied': {
    label: 'Permission required',
    variant: 'warning',
    icon: 'lock',
  },
};

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatMultiplier(v: number): string {
  return `${v.toFixed(2)}×`;
}

function formatZFactor(v: number): string {
  return Number.isInteger(v) ? `${v}×` : `${v.toFixed(1)}×`;
}

function bandIndexInputToValue(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(16, parsed));
}

function selectStyle(): CSSProperties {
  return {
    width: '100%',
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[1],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
}

function ColorSchemePreview({ rampId }: { rampId: RasterColorRamp }) {
  const preset = getRampPreset(rampId);
  return (
    <span
      aria-hidden="true"
      data-testid={`raster-ramp-preview-${rampId}`}
      style={{
        display: 'block',
        width: '100%',
        height: 12,
        borderRadius: tokens.radius.sm,
        background: preset.cssGradient,
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
      }}
    />
  );
}

function formatStretchLegendValue(value: number): string {
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (Math.abs(value) >= 10) return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function GreyscaleStretchLegend({
  min,
  max,
}: {
  readonly min: number | null;
  readonly max: number | null;
}) {
  if (
    typeof min !== 'number'
    || typeof max !== 'number'
    || !Number.isFinite(min)
    || !Number.isFinite(max)
    || max <= min
  ) {
    return null;
  }
  const values = [max, max - (max - min) * 0.25, (min + max) / 2, min + (max - min) * 0.25, min];
  return (
    <div
      data-testid="raster-greyscale-stretch-legend"
      aria-label={`Greyscale stretch: ${formatStretchLegendValue(min)} is black and ${formatStretchLegendValue(max)} is white`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[1],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <span
        style={{
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.role.label.size,
        }}
      >
        Value
      </span>
      <div style={{ display: 'flex', gap: tokens.space[2], alignItems: 'stretch' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'block',
            width: 30,
            minHeight: 112,
            background: 'linear-gradient(180deg, #ffffff 0%, #000000 100%)',
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
          }}
        />
        <div
          style={{
            minHeight: 112,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            color: tokens.color.ink.primary,
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.size.sm,
          }}
        >
          {values.map((value, index) => (
            <span key={index}>{formatStretchLegendValue(value)}</span>
          ))}
        </div>
      </div>
      <span
        style={{
          color: tokens.color.ink.muted,
          fontSize: tokens.font.role.engMeta.size,
        }}
      >
        Minimum is black; maximum is white. Values between them use a continuous linear shade.
      </span>
    </div>
  );
}

function CollapsibleSection({
  title,
  description,
  open,
  onToggle,
  testId,
  children,
}: {
  title: string;
  description?: string;
  open: boolean;
  onToggle: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        padding: tokens.space[2],
        background: open ? tokens.color.surface[1] : 'transparent',
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.input,
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: tokens.space[2],
          width: '100%',
          minHeight: tokens.density.touchMin,
          padding: `${tokens.space[1]}px ${tokens.space[1]}px`,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: tokens.font.family.ui,
        }}
      >
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span
            style={{
              margin: 0,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.label.size,
              fontWeight: tokens.font.weight.medium,
              lineHeight: tokens.font.role.label.lineHeight,
              color: tokens.color.ink.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {title}
          </span>
          {description && !open ? (
            <span
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.engMeta.size,
                color: tokens.color.ink.muted,
              }}
            >
              {description}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            width: 32,
            height: 32,
            color: tokens.color.ink.secondary,
            fontSize: 20,
            fontWeight: tokens.font.weight.semibold,
            lineHeight: 1,
          }}
        >
          {open ? '▼' : '▶'}
        </span>
      </button>
      {open ? (
        <>
          {description ? (
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.engMeta.size,
                color: tokens.color.ink.muted,
              }}
            >
              {description}
            </div>
          ) : null}
          {children}
        </>
      ) : null}
    </section>
  );
}

export function RasterToolCard({
  layer,
  tileHealth = 'rest',
  permissionDenied,
  readOnly,
  isElevationLike,
  engine,
  onTerrainViewChange,
  terrainViewSwitching = false,
  onPickClipMask,
  onOpenProfileSampling,
  forceVisible,
  container = null,
  initialOpenSections,
  onGeoreferenceCommit,
  'data-testid': testId,
}: RasterToolCardProps) {
  const toolCardKind = useViewerSlice(
    (s) => s.sceneSelection.toolCardKind ?? 'gtwin',
  );
  const state = useViewerSlice((s) => s.rasterToolCard);
  const camera = useViewerSlice((s) => s.camera);
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);
  const [openSections, setOpenSections] = useState(() => ({
    ...DEFAULT_OPEN_SECTIONS,
    ...initialOpenSections,
  }));

  const toggleSection = useCallback((id: DeferredSectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const showGeoreference = shouldShowGeoreferenceTools(layer ?? null);
  const georeferenceStatus = layer ? readGeoreference(layer)?.status : undefined;

  useEffect(() => {
    if (georeferenceStatus === 'rectifying') {
      setOpenSections((prev) => (prev.georeference ? prev : { ...prev, georeference: true }));
    }
  }, [georeferenceStatus, layer?.id]);

  // Keep hover sampling on while this section is open. Avoid clearing on
  // dependency churn (Strict Mode / HMR) so live Spectral stays responsive.
  useEffect(() => {
    setRasterToolCard({ liveSpectralEnabled: openSections.pixel });
  }, [openSections.pixel, setRasterToolCard]);

  useEffect(() => {
    return () => {
      setRasterToolCard({ liveSpectralEnabled: false });
    };
  }, [setRasterToolCard]);

  const tileChip = TILE_HEALTH_TO_CHIP[tileHealth];

  const updateBand = useCallback(
    (channelIdx: 0 | 1 | 2, nextValue: number) => {
      const bands: [number, number, number] = [...state.bands];
      bands[channelIdx] = nextValue;
      setRasterToolCard({ bands });
    },
    [state.bands, setRasterToolCard],
  );

  const selectRenderMode = useCallback(
    (mode: RasterRenderMode) => {
      setRasterToolCard(patchForRasterRenderMode(mode, state));
    },
    [setRasterToolCard, state],
  );

  const renderMode = normalizeRasterRenderMode(state.renderMode) ?? state.renderMode;
  const backgroundColorMode = state.backgroundColorMode ?? 'transparent';
  const tilesUrl = layer?.endpoints?.tiles ?? null;
  const statsKeyRef = useRef<string>('');
  const draTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Monotonic id of the newest statistics request. Panning can leave several in
   * flight, and TiTiler answers extent POSTs at wildly different speeds — an
   * earlier one landing last used to overwrite the stretch with a stale extent.
   */
  const statsSeqRef = useRef(0);

  // Populate stretch range / class breaks / unique values from TiTiler stats.
  // Dataset = full COG GET; DRA = POST clipped to the current display extent.
  useEffect(() => {
    if (!tilesUrl || !tilesUrl.includes('/cog/')) return;
    const needsStats =
      renderMode === 'stretch'
      || renderMode === 'classify'
      || renderMode === 'discrete'
      || renderMode === 'unique-values'
      || renderMode === 'shaded-relief';
    if (!needsStats) return;

    const parts = extractCogQuery(tilesUrl);
    if (!parts) return;

    const statisticsSource = state.statisticsSource ?? 'dataset';
    const useDra = statisticsSource === 'dra'
      && (renderMode === 'stretch' || renderMode === 'shaded-relief');

    const displayNodata = resolveDisplayNodata(
      {
        noDataMask: state.noDataMask,
        displayBackgroundValue: state.displayBackgroundValue ?? false,
        backgroundValue: state.backgroundValue ?? 0,
      },
      null,
    );

    const runFetch = (draBbox: LonLatBbox | null) => {
      const key = [
        parts.cogParam,
        renderMode,
        state.classCount,
        state.discreteColorCount,
        state.classifyMethod,
        state.colorRamp,
        state.stretchType,
        statisticsSource,
        draBbox ? bboxCacheKey(draBbox) : 'dataset',
        displayNodata ?? '',
      ].join('|');
      if (statsKeyRef.current === key) return;
      statsKeyRef.current = key;
      const seq = statsSeqRef.current + 1;
      statsSeqRef.current = seq;

      let cancelled = false;
      const baseUrl =
        `${parts.base}/cog/statistics?url=${parts.cogParam}&max_size=256&histogram_bins=64`;
      const withNodata =
        displayNodata != null
          ? `${baseUrl}&nodata=${encodeURIComponent(String(displayNodata))}`
          : baseUrl;

      // The extent is resolved once, by the caller, and used for both the key
      // and the body — reading it twice let them describe different views.
      const request = useDra && draBbox
        ? fetch(withNodata, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bboxToStatisticsGeoJson(draBbox)),
          })
        : fetch(withNodata);

      void request
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<Record<string, unknown>>;
        })
        .then((payload) => {
          if (cancelled || seq !== statsSeqRef.current) return;
          const bands = parseBandStats(payload);
          const band = bands[0];
          if (!band) return;
          const stats = {
            min: band.min,
            max: band.max,
            mean: band.mean,
            std: band.std,
            percentile2: band.percentile2,
            percentile98: band.percentile98,
            counts: band.counts,
            bins: band.bins,
          };
          const patch: Parameters<typeof setRasterToolCard>[0] = {
            symbologyDataMin: band.min,
            symbologyDataMax: band.max,
          };
          if (renderMode === 'stretch' || renderMode === 'shaded-relief') {
            const range = stretchRangeFromStats(stats, state.stretchType);
            if (range) {
              Object.assign(patch, { stretchMin: range[0], stretchMax: range[1] });
            }
          }
          if (renderMode === 'classify') {
            Object.assign(patch, { classBreaks: buildClassBreaks(
              stats,
              state.classCount,
              state.classifyMethod,
              state.colorRamp,
            ) });
          }
          if (renderMode === 'discrete') {
            Object.assign(patch, { classBreaks: buildClassBreaks(
              stats,
              state.discreteColorCount,
              'equal-interval',
              state.colorRamp,
            ) });
          }
          if (renderMode === 'unique-values') {
            Object.assign(patch, { uniqueValues: buildUniqueValueRows(stats, state.colorRamp) });
          }
          setRasterToolCard(patch);
        })
        .catch(() => {
          // Release the key so the next settle can retry. Parking a failed
          // request's key froze the stretch on whatever extent last succeeded.
          if (statsKeyRef.current === key) statsKeyRef.current = '';
          /* keep previous breaks; histogram panel shows its own error */
        });

      return () => {
        cancelled = true;
      };
    };

    if (!useDra) {
      return runFetch(null);
    }

    // Debounce camera / pan updates so DRA does not hammer TiTiler.
    if (draTimerRef.current) clearTimeout(draTimerRef.current);
    let cleanupFetch: (() => void) | undefined;
    draTimerRef.current = setTimeout(() => {
      // A null extent means no map host yet; dataset stats stand in until one
      // reports a viewport, and the key guard lets DRA take over on arrival.
      cleanupFetch = runFetch(resolveDraBbox(container, camera));
    }, DRA_DEBOUNCE_MS);

    return () => {
      if (draTimerRef.current) clearTimeout(draTimerRef.current);
      cleanupFetch?.();
    };
  }, [
    tilesUrl,
    renderMode,
    state.classCount,
    state.discreteColorCount,
    state.classifyMethod,
    state.colorRamp,
    state.stretchType,
    state.statisticsSource,
    state.noDataMask,
    state.displayBackgroundValue,
    state.backgroundValue,
    camera,
    container,
    setRasterToolCard,
  ]);

  const provenanceFacts = useMemo(() => {
    const facts: { label: string; value: string }[] = [];
    if (layer?.label) facts.push({ label: 'Layer', value: layer.label });
    if (layer?.id) facts.push({ label: 'Scene item', value: layer.id });
    if (layer?.kind) facts.push({ label: 'Kind', value: layer.kind });
    if (layer?.crs) facts.push({ label: 'CRS', value: layer.crs });
    if (layer?.bbox && layer.bbox.length === 4) {
      facts.push({
        label: 'BBox',
        value: layer.bbox.map((c) => c.toFixed(4)).join(', '),
      });
    }
    return facts;
  }, [layer]);

  const visible = forceVisible || toolCardKind === 'raster';
  if (!visible) return null;

  const showPermissionBanner = !!permissionDenied;
  const showTileChip = tileHealth !== 'rest';
  const showBandPicker = renderMode === 'multiband-color';
  const showColorRamp =
    renderMode === 'stretch'
    || renderMode === 'discrete'
    || renderMode === 'classify'
    || renderMode === 'shaded-relief'
    || renderMode === 'unique-values';
  const showStretchControls = renderMode === 'stretch' || renderMode === 'shaded-relief';
  const showZFactor = renderMode === 'hillshade' || renderMode === 'shaded-relief';
  const showClassifyTable = renderMode === 'classify' && state.classBreaks.length > 0;
  const showDiscreteCount = renderMode === 'discrete';
  const showUniqueTable = renderMode === 'unique-values' && state.uniqueValues.length > 0;

  return (
    <ToolCardShell
      icon="image"
      title="Raster / COG"
      subtitle={layer?.label ?? layer?.id ?? null}
      data-testid={testId ?? 'raster-toolcard'}
      topBanners={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
          }}
        >
          {isElevationLike && engine && onTerrainViewChange ? (
            <div data-testid="raster-terrain-view-switch">
              <TerrainViewSwitch
                current={engine}
                onChange={onTerrainViewChange}
                disabled={terrainViewSwitching}
              />
            </div>
          ) : null}
          {showPermissionBanner ? (
            <Banner
              severity="warning"
              title="Raster restricted"
              body={permissionDenied}
              narrationKey="raster-toolcard-permission"
            />
          ) : null}
          {readOnly ? (
            <Chip icon="visibility" variant="tertiary" label="Read-only" />
          ) : null}
          {showTileChip ? (
            <Chip
              icon={tileChip.icon}
              variant={tileChip.variant}
              label={tileChip.label}
            />
          ) : null}
        </div>
      }
      footer={
        provenanceFacts.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: `${tokens.space[1]}px ${tokens.space[2]}px`,
            }}
          >
            {provenanceFacts.map((fact) => (
              <div
                key={fact.label}
                style={{
                  display: 'contents',
                  fontFamily: tokens.font.family.mono,
                }}
              >
                <span style={{ color: tokens.color.ink.secondary }}>
                  {fact.label}
                </span>
                <span
                  style={{
                    fontFamily: tokens.font.family.mono,
                    color: tokens.color.ink.primary,
                  }}
                >
                  {fact.value}
                </span>
              </div>
            ))}
          </div>
        ) : null
      }
    >
      <CollapsibleSection
        title="Symbology"
        description="Choose a render type — only its controls are shown below."
        open={openSections.symbology}
        onToggle={() => toggleSection('symbology')}
        testId="raster-section-symbology"
      >
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[1],
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            color: tokens.color.ink.secondary,
          }}
        >
          Primary symbology
          <select
            aria-label="Primary symbology"
            data-testid="raster-render-mode-select"
            value={renderMode}
            onChange={(e) => selectRenderMode(e.currentTarget.value as RasterRenderMode)}
            style={selectStyle()}
          >
            {RASTER_RENDER_MODES.map((mode) => (
              <option key={mode.id} value={mode.id} data-testid={`raster-render-mode-${mode.id}`}>
                {mode.label}
              </option>
            ))}
          </select>
        </label>
        <div
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.muted,
          }}
        >
          {RASTER_RENDER_MODES.find((m) => m.id === renderMode)?.description}
        </div>

        {showBandPicker ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: tokens.space[2],
            }}
          >
            {(['R', 'G', 'B'] as const).map((channel, idx) => (
              <label
                key={channel}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.space[1],
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.label.size,
                  color: tokens.color.ink.secondary,
                }}
              >
                {channel}
                <input
                  type="number"
                  min={1}
                  max={16}
                  value={state.bands[idx as 0 | 1 | 2]}
                  aria-label={`${channel} band index`}
                  data-testid={`raster-band-${channel.toLowerCase()}`}
                  onChange={(e) =>
                    updateBand(
                      idx as 0 | 1 | 2,
                      bandIndexInputToValue(e.currentTarget.value),
                    )
                  }
                  style={{
                    appearance: 'none',
                    width: '100%',
                    minHeight: tokens.density.touchMin,
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    background: tokens.color.surface[1],
                    color: tokens.color.ink.primary,
                    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                    borderRadius: tokens.radius.input,
                    fontFamily: tokens.font.family.mono,
                    fontSize: tokens.font.role.engMono.size,
                  }}
                />
              </label>
            ))}
          </div>
        ) : null}

        {showColorRamp ? (
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.space[1],
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.label.size,
              color: tokens.color.ink.secondary,
            }}
          >
            Color scheme
            <select
              aria-label="Color scheme"
              data-testid="raster-color-scheme-select"
              value={state.colorRamp}
              onChange={(e) => {
                const rampId = e.currentTarget.value as RasterColorRamp;
                const patch: Parameters<typeof setRasterToolCard>[0] = {
                  colorRamp: rampId,
                };
                if (state.classBreaks.length > 0) {
                  Object.assign(patch, { classBreaks: recolorClassBreaks(state.classBreaks, rampId) });
                }
                setRasterToolCard(patch);
              }}
              style={selectStyle()}
            >
              {RASTER_COLOR_RAMP_PRESETS.map((ramp) => (
                <option key={ramp.id} value={ramp.id} data-testid={`raster-ramp-${ramp.id}`}>
                  {ramp.label}
                </option>
              ))}
            </select>
            <ColorSchemePreview rampId={state.colorRamp} />
          </label>
        ) : null}

        {showZFactor ? (
          <ToolCardSliderRow
            label="Z-factor"
            icon="swap_vert"
            min={0.1}
            max={10}
            step={0.1}
            value={state.hillshadeZFactor}
            readout={formatZFactor(state.hillshadeZFactor)}
            onChange={(next) => setRasterToolCard({ hillshadeZFactor: next })}
            data-testid="raster-hillshade-zfactor-slider"
          />
        ) : null}

        {showStretchControls ? (
          <>
            <div
              role="radiogroup"
              aria-label="Statistics source"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
              }}
            >
              <span
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.label.size,
                  color: tokens.color.ink.secondary,
                }}
              >
                Statistics
              </span>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: tokens.space[1],
                }}
              >
                {STATISTICS_SOURCES.map((opt) => (
                  <Chip
                    key={opt.id}
                    icon={opt.id === 'dra' ? 'tune' : 'database'}
                    variant={
                      (state.statisticsSource ?? 'dataset') === opt.id
                        ? 'info'
                        : 'neutral'
                    }
                    label={opt.label}
                    pressable
                    pressed={(state.statisticsSource ?? 'dataset') === opt.id}
                    onClick={() => setRasterToolCard({ statisticsSource: opt.id })}
                    data-testid={`raster-statistics-${opt.id}`}
                  />
                ))}
              </div>
              <span
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.engMeta.size,
                  color: tokens.color.ink.muted,
                }}
              >
                {
                  STATISTICS_SOURCES.find(
                    (s) => s.id === (state.statisticsSource ?? 'dataset'),
                  )?.description
                }
              </span>
            </div>
            <div
              role="radiogroup"
              aria-label="Stretch type"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: tokens.space[1],
              }}
            >
              {STRETCH_TYPES.map((opt) => (
                <Chip
                  key={opt.id}
                  icon="tune"
                  variant={state.stretchType === opt.id ? 'info' : 'neutral'}
                  label={opt.label}
                  pressable
                  pressed={state.stretchType === opt.id}
                  onClick={() => setRasterToolCard({ stretchType: opt.id })}
                  data-testid={`raster-stretch-${opt.id}`}
                />
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: tokens.space[2],
              }}
            >
              {(['Min', 'Max'] as const).map((label, idx) => {
                const value = idx === 0 ? state.stretchMin : state.stretchMax;
                return (
                  <label
                    key={label}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: tokens.space[1],
                      fontSize: tokens.font.role.label.size,
                      color: tokens.color.ink.secondary,
                      fontFamily: tokens.font.family.ui,
                    }}
                  >
                    {label}
                    <input
                      type="number"
                      value={value ?? ''}
                      data-testid={`raster-stretch-${label.toLowerCase()}`}
                      onChange={(e) => {
                        const parsed = Number.parseFloat(e.currentTarget.value);
                        const next = Number.isFinite(parsed) ? parsed : null;
                        setRasterToolCard(
                          idx === 0 ? { stretchMin: next } : { stretchMax: next },
                        );
                      }}
                      style={{
                        appearance: 'none',
                        width: '100%',
                        minHeight: tokens.density.touchMin,
                        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                        background: tokens.color.surface[1],
                        color: tokens.color.ink.primary,
                        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                        borderRadius: tokens.radius.input,
                        fontFamily: tokens.font.family.mono,
                      }}
                    />
                  </label>
                );
              })}
            </div>
            {state.colorRamp === 'greyscale' ? (
              <GreyscaleStretchLegend min={state.stretchMin} max={state.stretchMax} />
            ) : null}
          </>
        ) : null}

        {showDiscreteCount ? (
          <>
            <ToolCardSliderRow
              label="Colors"
              icon="palette"
              min={2}
              max={32}
              step={1}
              value={state.discreteColorCount}
              readout={String(state.discreteColorCount)}
              onChange={(next) => setRasterToolCard({ discreteColorCount: Math.round(next) })}
              data-testid="raster-discrete-count-slider"
            />
            {state.classBreaks.length > 0 ? (
              <span
                aria-hidden="true"
                data-testid="raster-discrete-preview"
                style={{
                  display: 'block',
                  width: '100%',
                  height: 12,
                  borderRadius: tokens.radius.sm,
                  background: classBreaksCssGradient(state.classBreaks),
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                }}
              />
            ) : null}
          </>
        ) : null}

        {renderMode === 'classify' ? (
          <>
            <ToolCardSliderRow
              label="Classes"
              icon="filter_list"
              min={2}
              max={32}
              step={1}
              value={state.classCount}
              readout={String(state.classCount)}
              onChange={(next) => setRasterToolCard({ classCount: Math.round(next) })}
              data-testid="raster-class-count-slider"
            />
            <div
              role="radiogroup"
              aria-label="Classify method"
              style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[1] }}
            >
              {(
                [
                  { id: 'equal-interval' as const, label: 'Equal interval' },
                  { id: 'quantile' as const, label: 'Quantile' },
                  { id: 'manual' as const, label: 'Manual' },
                ] as const
              ).map((opt) => (
                <Chip
                  key={opt.id}
                  icon="category"
                  variant={state.classifyMethod === opt.id ? 'info' : 'neutral'}
                  label={opt.label}
                  pressable
                  pressed={state.classifyMethod === opt.id}
                  onClick={() => setRasterToolCard({ classifyMethod: opt.id })}
                  data-testid={`raster-classify-${opt.id}`}
                />
              ))}
            </div>
            {showClassifyTable ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.space[1],
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {state.classBreaks.map((row, index) => (
                  <div
                    key={row.id}
                    data-testid={`raster-class-row-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(108px, 1.1fr) 1fr 1fr',
                      gap: tokens.space[1],
                      alignItems: 'center',
                    }}
                  >
                    <RasterColorField
                      value={row.color}
                      aria-label={`Class ${index + 1} color`}
                      data-testid={`raster-class-color-${index}`}
                      onChange={(color) => {
                        setRasterToolCard({
                          classBreaks: state.classBreaks.map((b) =>
                            b.id === row.id ? { ...b, color } : b,
                          ),
                          classifyMethod: 'manual',
                        });
                      }}
                    />
                    <input
                      type="number"
                      value={row.upperValue}
                      aria-label={`Class ${index + 1} upper value`}
                      onChange={(e) => {
                        const upperValue = Number.parseFloat(e.currentTarget.value);
                        if (!Number.isFinite(upperValue)) return;
                        setRasterToolCard({
                          classBreaks: state.classBreaks.map((b) =>
                            b.id === row.id ? { ...b, upperValue } : b,
                          ),
                          classifyMethod: 'manual',
                        });
                      }}
                      style={{
                        minHeight: tokens.density.touchMin,
                        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                        background: tokens.color.surface[1],
                        color: tokens.color.ink.primary,
                        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                        borderRadius: tokens.radius.input,
                        fontFamily: tokens.font.family.mono,
                      }}
                    />
                    <input
                      type="text"
                      value={row.label}
                      aria-label={`Class ${index + 1} label`}
                      onChange={(e) => {
                        const label = e.currentTarget.value;
                        setRasterToolCard({
                          classBreaks: state.classBreaks.map((b) =>
                            b.id === row.id ? { ...b, label } : b,
                          ),
                          classifyMethod: 'manual',
                        });
                      }}
                      style={{
                        minHeight: tokens.density.touchMin,
                        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                        background: tokens.color.surface[1],
                        color: tokens.color.ink.primary,
                        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                        borderRadius: tokens.radius.input,
                        fontFamily: tokens.font.family.ui,
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Callout severity="info" icon="info">
                Loading class breaks from raster statistics…
              </Callout>
            )}
          </>
        ) : null}

        {showUniqueTable ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.space[1],
              maxHeight: 240,
              overflowY: 'auto',
            }}
          >
            {state.uniqueValues.map((row, index) => (
              <div
                key={row.id}
                data-testid={`raster-unique-row-${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(108px, 1.2fr) auto 1fr',
                  gap: tokens.space[1],
                  alignItems: 'center',
                }}
              >
                <RasterColorField
                  value={row.color}
                  aria-label={`Value ${row.value} color`}
                  data-testid={`raster-unique-color-${index}`}
                  onChange={(color) => {
                    setRasterToolCard({
                      uniqueValues: state.uniqueValues.map((b) =>
                        b.id === row.id ? { ...b, color } : b,
                      ),
                    });
                  }}
                />
                <span
                  style={{
                    fontFamily: tokens.font.family.mono,
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.secondary,
                  }}
                >
                  {row.value}
                </span>
                <input
                  type="text"
                  value={row.label}
                  aria-label={`Value ${row.value} label`}
                  onChange={(e) => {
                    const label = e.currentTarget.value;
                    setRasterToolCard({
                      uniqueValues: state.uniqueValues.map((b) =>
                        b.id === row.id ? { ...b, label } : b,
                      ),
                    });
                  }}
                  style={{
                    minHeight: tokens.density.touchMin,
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    background: tokens.color.surface[1],
                    color: tokens.color.ink.primary,
                    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                    borderRadius: tokens.radius.input,
                    fontFamily: tokens.font.family.ui,
                  }}
                />
              </div>
            ))}
          </div>
        ) : null}

        {renderMode === 'hillshade' ? (
          <Callout severity="info" icon="info">
            Grayscale shaded relief matching QGIS / <code>gdaldem hillshade</code>
            {' '}(azimuth 315°, altitude 45°). Prefer Shaded Relief when you want
            an elevation color scheme under the lighting.
          </Callout>
        ) : null}
        {renderMode === 'shaded-relief' ? (
          <Callout severity="info" icon="info">
            Elevation color ramp merged with hillshade lighting (ArcGIS Shaded
            Relief). Pick a color scheme above; Z-factor exaggerates relief.
          </Callout>
        ) : null}
        {renderMode === 'vector-field' ? (
          <Callout severity="info" icon="info">
            Vector Field symbology (magnitude/direction or U/V arrows) is not
            wired yet. Stretch or Classify the magnitude band for now.
          </Callout>
        ) : null}
      </CollapsibleSection>

      {showGeoreference && layer ? (
        <CollapsibleSection
          title="Georeference"
          description="Overlap this image on the earth with matching control points."
          open={openSections.georeference}
          onToggle={() => toggleSection('georeference')}
          testId="raster-section-georeference"
        >
          <GeoreferencePanel
            layer={layer}
            container={container}
            readOnly={readOnly}
            onCommit={onGeoreferenceCommit}
          />
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        title="Display"
        open={openSections.display}
        onToggle={() => toggleSection('display')}
        testId="raster-section-display"
      >
        <ToolCardSliderRow
          label="Opacity"
          icon="tune"
          min={0}
          max={1}
          step={0.05}
          value={state.opacity}
          readout={formatPercent(state.opacity)}
          onChange={(next) => setRasterToolCard({ opacity: next })}
          data-testid="raster-opacity-slider"
        />
        <ToolCardSliderRow
          label="Brightness"
          icon="flare"
          min={0.5}
          max={1.5}
          step={0.05}
          value={state.brightness}
          readout={formatMultiplier(state.brightness)}
          onChange={(next) => setRasterToolCard({ brightness: next })}
          data-testid="raster-brightness-slider"
        />
        <ToolCardSliderRow
          label="Contrast"
          icon="contrast"
          min={0.5}
          max={1.5}
          step={0.05}
          value={state.contrast}
          readout={formatMultiplier(state.contrast)}
          onChange={(next) => setRasterToolCard({ contrast: next })}
          data-testid="raster-contrast-slider"
        />
        <ToolCardSliderRow
          label="Saturation"
          icon="colorize"
          min={0}
          max={2}
          step={0.05}
          value={state.saturation}
          readout={formatMultiplier(state.saturation)}
          onChange={(next) => setRasterToolCard({ saturation: next })}
          data-testid="raster-saturation-slider"
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Blending & no-data"
        open={openSections.blending}
        onToggle={() => toggleSection('blending')}
        testId="raster-section-blending"
      >
        <ToolCardToggleRow
          label="No-data mask"
          description="Hide pixels that match the raster's no-data sentinel."
          icon="block"
          checked={state.noDataMask}
          onChange={(next) => setRasterToolCard({ noDataMask: next })}
          data-testid="raster-nodata-toggle"
        />
        <ToolCardToggleRow
          label="Display background value"
          description="Treat a fill value (often 0) as transparent — removes DEM/DTM black borders."
          icon="crop_free"
          checked={state.displayBackgroundValue}
          onChange={(next) => setRasterToolCard({ displayBackgroundValue: next })}
          data-testid="raster-background-toggle"
        />
        {state.displayBackgroundValue ? (
          <>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                fontFamily: tokens.font.family.ui,
              }}
            >
              Background value
              <input
                type="number"
                value={state.backgroundValue}
                onChange={(e) => {
                  const next = Number(e.currentTarget.value);
                  if (!Number.isFinite(next)) return;
                  setRasterToolCard({ backgroundValue: next });
                }}
                data-testid="raster-background-value"
                style={{
                  ...selectStyle(),
                  minHeight: tokens.density.touchMin,
                }}
              />
            </label>
            <ToolCardSliderRow
              label="Edge tolerance"
              icon="tune"
              min={0}
              max={48}
              step={1}
              value={state.backgroundTolerance}
              readout={`±${Math.round(state.backgroundTolerance)}`}
              onChange={(next) =>
                setRasterToolCard({ backgroundTolerance: Math.round(next) })
              }
              data-testid="raster-background-tolerance"
            />
            <span
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.engMeta.size,
                color: tokens.color.ink.muted,
              }}
            >
              Resampling leaves near-background pixels along the collar. Raise
              tolerance until the fringe clears; lower it if dark valleys start
              disappearing.
            </span>
            <div
              role="radiogroup"
              aria-label="Border color"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
              }}
            >
              <span
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.label.size,
                  color: tokens.color.ink.secondary,
                }}
              >
                Border color
              </span>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: tokens.space[1],
                }}
              >
                {BACKGROUND_COLOR_MODES.map((opt) => (
                  <Chip
                    key={opt.id}
                    icon={opt.icon}
                    variant={backgroundColorMode === opt.id ? 'info' : 'neutral'}
                    label={opt.label}
                    pressable
                    pressed={backgroundColorMode === opt.id}
                    onClick={() =>
                      setRasterToolCard({ backgroundColorMode: opt.id })
                    }
                    data-testid={`raster-background-color-mode-${opt.id}`}
                  />
                ))}
              </div>
              {backgroundColorMode === 'color' ? (
                <RasterColorField
                  value={state.backgroundColor ?? DEFAULT_BACKGROUND_COLOR}
                  onChange={(next) => setRasterToolCard({ backgroundColor: next })}
                  aria-label="Border color"
                  data-testid="raster-background-color"
                />
              ) : null}
              <span
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.engMeta.size,
                  color: tokens.color.ink.muted,
                }}
              >
                {backgroundColorMode === 'color'
                  ? 'The border and no-data area are painted this color instead of being hidden.'
                  : 'The border is hidden so layers underneath show through.'}
              </span>
            </div>
          </>
        ) : null}
        <div
          role="radiogroup"
          aria-label="Blend mode"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[1],
          }}
        >
          {BLEND_MODES.map((mode) => (
            <Chip
              key={mode.id}
              icon="gradient"
              variant={state.blending === mode.id ? 'info' : 'neutral'}
              label={mode.label}
              pressable
              pressed={state.blending === mode.id}
              onClick={() => setRasterToolCard({ blending: mode.id })}
              data-testid={`raster-blend-${mode.id}`}
            />
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Location & Spectral"
        description="Live cursor sample — expand to enable hover sampling."
        open={openSections.pixel}
        onToggle={() => toggleSection('pixel')}
        testId="raster-section-pixel"
      >
        <RasterSpectralProfile
          tilesUrl={tilesUrl}
          layerCrs={layer?.crs ?? layer?.sourceCrs ?? null}
          enabled={openSections.pixel}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Histogram & statistics"
        description="Loads on demand — expand to fetch band statistics."
        open={openSections.histogram}
        onToggle={() => toggleSection('histogram')}
        testId="raster-section-histogram"
      >
        <HistogramPanel enabled={openSections.histogram} />
      </CollapsibleSection>

      {isElevationLike ? (
        <CollapsibleSection
          title="Path profile"
          description="Draw a path and inspect height or depth along it."
          open={openSections.pathProfile}
          onToggle={() => toggleSection('pathProfile')}
          testId="raster-section-path-profile"
        >
          <PathProfilePanel
            container={container}
            tilesUrl={tilesUrl}
            enabled={openSections.pathProfile}
          />
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        title="Sampling & clip"
        open={openSections.sampling}
        onToggle={() => toggleSection('sampling')}
        testId="raster-section-sampling"
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
          }}
        >
          {isElevationLike && onOpenProfileSampling ? (
            <button
              type="button"
              onClick={onOpenProfileSampling}
              aria-label="Open elevation profile sampling"
              data-testid="raster-open-profile"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                minHeight: tokens.density.touchMin,
                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                background: tokens.color.surface[1],
                color: tokens.color.ink.primary,
                border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                borderRadius: tokens.radius.input,
                cursor: 'pointer',
              }}
            >
              Profile sampling
            </button>
          ) : null}
          <button
            type="button"
            onClick={onPickClipMask}
            disabled={readOnly || !onPickClipMask}
            aria-label="Pick clip or mask geometry"
            data-testid="raster-pick-clip-mask"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: !readOnly && onPickClipMask ? 'pointer' : 'default',
              opacity: !readOnly && onPickClipMask ? 1 : 0.5,
            }}
          >
            Clip / mask…
          </button>
        </div>
        {!onPickClipMask ? (
          <Callout severity="info" icon="info">
            Clip / mask by geometry will land alongside vector ToolCard
            (WP-4) so it can pick a selection from a feature layer.
          </Callout>
        ) : null}
      </CollapsibleSection>

      <CollapsibleSection
        title="Information"
        description="Loads on demand — expand to read the COG header."
        open={openSections.information}
        onToggle={() => toggleSection('information')}
        testId="raster-section-information"
      >
        <RasterMetadataPanel layer={layer} embedded enabled={openSections.information} />
      </CollapsibleSection>
    </ToolCardShell>
  );
}
